import { useState, useEffect } from "react";
import axios from "axios";
import {
  MapPin, Plus, Loader2, CheckCircle2, AlertTriangle, Star,
  Phone, Globe, Trash2, Edit3, X, Save, TrendingUp, Award,
  RefreshCw, Building2, Camera, MessageSquare, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const httpAuth = () => ({ Authorization: `Bearer ${localStorage.getItem("wt_token")}` });

const BAND_STYLE = {
  "needs-work":   { chip: "bg-rose-500/25 text-rose-100 ring-rose-400/40", bar: "bg-rose-400" },
  "good":         { chip: "bg-amber-500/25 text-amber-100 ring-amber-400/40", bar: "bg-amber-400" },
  "great":        { chip: "bg-emerald-500/25 text-emerald-100 ring-emerald-400/40", bar: "bg-emerald-400" },
  "world-class":  { chip: "bg-fuchsia-500/25 text-fuchsia-100 ring-fuchsia-400/40", bar: "bg-fuchsia-400" },
};

const HEALTH_STYLE = {
  "strong":   { label: "Strong",   color: "text-emerald-300" },
  "steady":   { label: "Steady",   color: "text-sky-300" },
  "thin":     { label: "Thin",     color: "text-amber-300" },
  "starting": { label: "Starting", color: "text-rose-300" },
  "aging":    { label: "Aging",    color: "text-amber-400" },
  "unknown":  { label: "Unknown",  color: "text-white/50" },
};

/**
 * /local-presence — multi-location dashboard.
 * Owners see per-store completeness score, review health, gaps,
 * and can add/edit locations inline.
 */
export default function LocalPresence() {
  const [locations, setLocations] = useState(null);
  const [summary, setSummary] = useState(null);
  const [editing, setEditing] = useState(null);   // location object or "new"
  const [busy, setBusy] = useState("");
  const [authError, setAuthError] = useState("");

  const load = async () => {
    setAuthError("");
    try {
      const [r1, r2] = await Promise.all([
        axios.get(`${API}/local-presence/locations`, { headers: httpAuth() }),
        axios.get(`${API}/local-presence/summary`,   { headers: httpAuth() }),
      ]);
      setLocations(r1.data.locations || []);
      setSummary(r2.data);
    } catch (e) {
      if (e?.response?.status === 401 || e?.response?.status === 403) {
        setAuthError("Please sign in to manage locations.");
      } else toast.error("Failed to load");
      setLocations([]);
    }
  };

  useEffect(() => { load(); }, []);

  const recheck = async (id) => {
    setBusy(id);
    try {
      await axios.post(`${API}/local-presence/locations/${id}/gbp-check`,
        {}, { headers: httpAuth() });
      toast.success("Rescored");
      load();
    } catch { toast.error("Failed"); }
    finally { setBusy(""); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this location?")) return;
    setBusy(id);
    try {
      await axios.delete(`${API}/local-presence/locations/${id}`,
        { headers: httpAuth() });
      toast.success("Deleted");
      load();
    } catch { toast.error("Failed"); }
    finally { setBusy(""); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-emerald-950 to-slate-900 text-white pb-20"
         data-testid="local-presence">
      <div className="max-w-6xl mx-auto px-6 pt-14">
        <div className="text-[10px] uppercase tracking-[0.35em] text-emerald-300 font-bold">
          WebsiteTech · Local Presence
        </div>
        <div className="flex items-end justify-between gap-4 flex-wrap mt-3">
          <div>
            <h1 className="text-4xl sm:text-5xl font-bold">Multi-location dashboard</h1>
            <p className="mt-2 text-white/70 max-w-2xl">
              Per-store completeness scoring across 15 GBP signals.
              Fix the gaps and get in the map-3-pack.
            </p>
          </div>
          <div className="flex gap-2 items-end">
            <button onClick={async () => {
                      const url = prompt("Paste a business website URL to auto-fill a new location:");
                      if (!url) return;
                      try {
                        const { data } = await axios.post(`${API}/local-presence/import-from-url`,
                          { url, save: false }, { headers: httpAuth() });
                        setEditing({
                          ...data.parsed,
                          _prefilled_from_url: url,
                        });
                        toast.success(`Pre-filled from ${data.parsed._source === "jsonld" ? "structured data" : "page content"}`);
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || "Import failed");
                      }
                    }}
                    data-testid="lp-import-url-btn"
                    className="rounded-xl bg-white/10 hover:bg-white/20 text-white px-5 py-3 font-bold inline-flex items-center gap-2">
              <Globe className="w-4 h-4" /> Import from URL
            </button>
            <button onClick={() => setEditing("new")}
                    data-testid="lp-add-btn"
                    className="rounded-xl bg-gradient-to-r from-emerald-500 to-teal-400 hover:brightness-110 text-white px-5 py-3 font-bold inline-flex items-center gap-2">
              <Plus className="w-4 h-4" /> Add location
            </button>
          </div>
        </div>

        {authError && (
          <div className="mt-8 rounded-2xl bg-amber-500/10 ring-1 ring-amber-500/30 text-amber-100 p-6 max-w-2xl">
            {authError}
          </div>
        )}

        {summary && summary.totals?.locations > 0 && (
          <div className="mt-8 grid sm:grid-cols-2 md:grid-cols-5 gap-3" data-testid="lp-summary">
            <Kpi label="Locations" value={summary.totals.locations} icon={MapPin} tone="emerald" />
            <Kpi label="Avg score" value={summary.totals.avg_score ?? "—"} icon={TrendingUp} tone="fuchsia" />
            <Kpi label="World-class" value={summary.totals.world_class} icon={Award} tone="amber" />
            <Kpi label="Total reviews" value={summary.totals.total_reviews} icon={MessageSquare} tone="sky" />
            <Kpi label="Avg rating" value={summary.totals.avg_rating ?? "—"} icon={Star} tone="orange" />
          </div>
        )}

        {locations === null && (
          <div className="mt-10 flex items-center gap-2 text-white/60">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        )}

        {locations && locations.length === 0 && !authError && (
          <div className="mt-16 text-center">
            <MapPin className="w-10 h-10 mx-auto text-emerald-300" />
            <div className="mt-3 text-xl font-bold">No locations yet</div>
            <div className="text-white/60">Add your first store to start tracking completeness.</div>
            <button onClick={() => setEditing("new")}
                     data-testid="lp-first-btn"
                     className="mt-4 inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-400 text-white px-5 py-3 font-bold">
              <Plus className="w-4 h-4" /> Add location
            </button>
          </div>
        )}

        {locations && locations.length > 0 && (
          <div className="mt-8 grid md:grid-cols-2 gap-4" data-testid="lp-grid">
            {locations.map((loc) => (
              <LocationCard key={loc.id} loc={loc}
                             busy={busy === loc.id}
                             onEdit={() => setEditing(loc)}
                             onRecheck={() => recheck(loc.id)}
                             onDelete={() => remove(loc.id)} />
            ))}
          </div>
        )}
      </div>

      {editing && (
        <LocationEditor value={editing === "new" ? null : editing}
                          onClose={() => setEditing(null)}
                          onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}


function Kpi({ label, value, icon: Icon, tone }) {
  const tint = {
    emerald: "bg-emerald-500/15 ring-emerald-400/30 text-emerald-100",
    fuchsia: "bg-fuchsia-500/15 ring-fuchsia-400/30 text-fuchsia-100",
    amber:   "bg-amber-500/15 ring-amber-400/30 text-amber-100",
    sky:     "bg-sky-500/15 ring-sky-400/30 text-sky-100",
    orange:  "bg-orange-500/15 ring-orange-400/30 text-orange-100",
  }[tone];
  return (
    <div className={`rounded-xl ring-1 p-4 ${tint}`}>
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4" />
        <span className="text-[10px] uppercase tracking-widest font-bold">{label}</span>
      </div>
      <div className="mt-1 text-3xl font-extrabold">{value}</div>
    </div>
  );
}


function LocationCard({ loc, busy, onEdit, onRecheck, onDelete }) {
  const band = BAND_STYLE[loc.completeness_band] || BAND_STYLE["needs-work"];
  const health = HEALTH_STYLE[loc.review_health] || HEALTH_STYLE.unknown;
  const gaps = (loc.completeness_gaps || []).slice(0, 4);
  return (
    <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 backdrop-blur p-5"
         data-testid={`lp-card-${loc.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-xl font-bold truncate flex items-center gap-2">
            <Building2 className="w-4 h-4 text-emerald-300 shrink-0" />
            {loc.business_name}
          </h3>
          <div className="text-xs text-white/60 mt-1 flex items-center gap-1 truncate">
            <MapPin className="w-3 h-3 shrink-0" />
            {[loc.city, loc.state, loc.zip].filter(Boolean).join(", ") || loc.address || "no address"}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={onRecheck} disabled={busy}
                  data-testid={`lp-recheck-${loc.id}`}
                  className="text-white/60 hover:text-white p-1.5 rounded-lg hover:bg-white/10">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          </button>
          <button onClick={onEdit}
                  data-testid={`lp-edit-${loc.id}`}
                  className="text-white/60 hover:text-white p-1.5 rounded-lg hover:bg-white/10">
            <Edit3 className="w-3.5 h-3.5" />
          </button>
          <button onClick={onDelete} disabled={busy}
                  data-testid={`lp-delete-${loc.id}`}
                  className="text-rose-300 hover:text-rose-100 p-1.5 rounded-lg hover:bg-rose-500/20">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Score bar */}
      <div className="mt-4">
        <div className="flex items-center justify-between text-xs mb-1">
          <span className="text-white/60">GBP completeness</span>
          <span className={`text-[10px] rounded-full px-2 py-0.5 ring-1 font-bold uppercase tracking-widest ${band.chip}`}>
            {loc.completeness_band}
          </span>
        </div>
        <div className="h-2 rounded-full bg-white/10 overflow-hidden">
          <div className={`h-full ${band.bar} transition-all duration-500`}
                style={{ width: `${loc.completeness_score || 0}%` }} />
        </div>
        <div className="mt-1 text-xs text-white/50">{loc.completeness_score || 0} / 100</div>
      </div>

      {/* Reviews */}
      <div className="mt-4 grid grid-cols-3 gap-2 text-center">
        <Mini label="Reviews" value={loc.reviews_count ?? "—"} />
        <Mini label="Rating" value={loc.reviews_avg ? `${loc.reviews_avg}★` : "—"} />
        <Mini label="Health" value={health.label} tint={health.color} />
      </div>

      {/* Top gaps */}
      {gaps.length > 0 && (
        <div className="mt-4">
          <div className="text-[10px] uppercase tracking-widest font-bold text-amber-300 mb-2 flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" /> Top gaps
          </div>
          <ul className="space-y-1.5 text-xs">
            {gaps.map((g, i) => (
              <li key={i} className="flex items-start gap-1.5 text-white/70">
                <span className="mt-1 w-1 h-1 rounded-full bg-amber-300 shrink-0" />
                <span>{g.hint || g.field}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-4 pt-4 border-t border-white/10 flex items-center gap-2 text-xs text-white/60">
        {loc.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{loc.phone}</span>}
        {loc.website && (
          <a href={loc.website} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1 hover:text-white truncate">
            <Globe className="w-3 h-3" /> site
          </a>
        )}
        {loc.gbp_url && (
          <a href={loc.gbp_url} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1 hover:text-white">
            <ExternalLink className="w-3 h-3" /> GBP
          </a>
        )}
      </div>
    </div>
  );
}


function Mini({ label, value, tint }) {
  return (
    <div className="rounded-lg bg-white/5 py-2">
      <div className="text-[9px] uppercase tracking-widest text-white/50 font-bold">{label}</div>
      <div className={`text-lg font-bold ${tint || "text-white"}`}>{value}</div>
    </div>
  );
}


function LocationEditor({ value, onClose, onSaved }) {
  const isNew = !value;
  const [form, setForm] = useState(value || {
    business_name: "", address: "", city: "", state: "", zip: "",
    country: "USA", phone: "", website: "", gbp_url: "",
    categories: [], hours: {}, service_area: [],
    photos_count: 0, reviews_count: 0, reviews_avg: null,
    verified: false, posts_last_30d: 0,
  });
  const [busy, setBusy] = useState(false);

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.business_name?.trim()) { toast.error("Business name required"); return; }
    setBusy(true);
    try {
      const payload = { ...form,
        photos_count: Number(form.photos_count) || 0,
        reviews_count: Number(form.reviews_count) || 0,
        reviews_avg: form.reviews_avg ? Number(form.reviews_avg) : null,
        posts_last_30d: Number(form.posts_last_30d) || 0,
        categories: typeof form.categories === "string"
          ? form.categories.split(",").map(s => s.trim()).filter(Boolean)
          : (form.categories || []),
        service_area: typeof form.service_area === "string"
          ? form.service_area.split(",").map(s => s.trim()).filter(Boolean)
          : (form.service_area || []),
      };
      if (isNew) {
        await axios.post(`${API}/local-presence/locations`, payload,
          { headers: httpAuth() });
      } else {
        await axios.put(`${API}/local-presence/locations/${value.id}`, payload,
          { headers: httpAuth() });
      }
      toast.success("Saved");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-start sm:items-center justify-center p-4 overflow-auto"
         onClick={onClose} data-testid="lp-editor-backdrop">
      <div className="max-w-2xl w-full my-8 bg-gradient-to-br from-slate-900 via-emerald-950 to-slate-900 ring-1 ring-white/10 rounded-3xl p-6 text-white"
           onClick={(e) => e.stopPropagation()}
           data-testid="lp-editor">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold">{isNew ? "Add location" : "Edit location"}</h2>
          <button onClick={onClose} className="p-1 text-white/60 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <F label="Business name *" value={form.business_name} onChange={v => upd("business_name", v)} testid="lp-f-business-name" />
          <F label="Phone" value={form.phone} onChange={v => upd("phone", v)} testid="lp-f-phone" />
          <F label="Address" value={form.address} onChange={v => upd("address", v)} testid="lp-f-address" wide />
          <F label="City" value={form.city} onChange={v => upd("city", v)} testid="lp-f-city" />
          <F label="State" value={form.state} onChange={v => upd("state", v)} testid="lp-f-state" />
          <F label="ZIP" value={form.zip} onChange={v => upd("zip", v)} testid="lp-f-zip" />
          <F label="Country" value={form.country} onChange={v => upd("country", v)} testid="lp-f-country" />
          <F label="Website URL" value={form.website} onChange={v => upd("website", v)} testid="lp-f-website" wide />
          <F label="Google Business URL" value={form.gbp_url} onChange={v => upd("gbp_url", v)} testid="lp-f-gbp" wide />
          <F label="Categories (comma-sep)"
              value={Array.isArray(form.categories) ? form.categories.join(", ") : form.categories}
              onChange={v => upd("categories", v)} testid="lp-f-cats" wide />
          <F label="Service area cities (comma-sep)"
              value={Array.isArray(form.service_area) ? form.service_area.join(", ") : form.service_area}
              onChange={v => upd("service_area", v)} testid="lp-f-service-area" wide />
          <F label="Photos count" type="number" value={form.photos_count} onChange={v => upd("photos_count", v)} testid="lp-f-photos" />
          <F label="Reviews count" type="number" value={form.reviews_count} onChange={v => upd("reviews_count", v)} testid="lp-f-reviews" />
          <F label="Rating (1-5)" type="number" step="0.1" value={form.reviews_avg ?? ""} onChange={v => upd("reviews_avg", v)} testid="lp-f-rating" />
          <F label="Posts last 30 days" type="number" value={form.posts_last_30d} onChange={v => upd("posts_last_30d", v)} testid="lp-f-posts" />
          <label className="flex items-center gap-2 mt-2 sm:col-span-2">
            <input type="checkbox" checked={!!form.verified}
                    onChange={(e) => upd("verified", e.target.checked)}
                    data-testid="lp-f-verified"
                    className="w-4 h-4 accent-emerald-500" />
            <span className="text-sm">Verified in Google Business</span>
          </label>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onClose}
                  className="rounded-lg bg-white/10 hover:bg-white/20 px-4 py-2 text-sm font-bold">
            Cancel
          </button>
          <button onClick={save} disabled={busy}
                  data-testid="lp-save-btn"
                  className="rounded-lg bg-gradient-to-r from-emerald-500 to-teal-400 hover:brightness-110 text-white px-5 py-2 text-sm font-bold inline-flex items-center gap-2 disabled:opacity-50">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {isNew ? "Add" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}


function F({ label, value, onChange, type = "text", wide, testid, step }) {
  return (
    <label className={`block ${wide ? "sm:col-span-2" : ""}`}>
      <span className="text-[10px] uppercase tracking-widest text-white/50 font-bold mb-1 block">
        {label}
      </span>
      <input type={type} step={step} value={value ?? ""}
              onChange={(e) => onChange(e.target.value)}
              data-testid={testid}
              className="w-full rounded-lg bg-white/10 ring-1 ring-white/10 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400" />
    </label>
  );
}
